import { ChevronDown, Plus, SwitchVertical01 } from "@untitledui/icons";
import { type ReactNode, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { useAppTranslation } from "@/hooks/use-app-translation";
import type { AdminFormI18nPaths } from "../../admin-form-editor-i18n-paths";
import type { FormRule } from "../../types/form-rule-types";
import { FormRuleCard } from "./form-rule-card";
import { FormRulesFeaturedIcon } from "./form-rules-featured-icon";

const RuleFilter = {
	all: "all",
	enabled: "enabled",
	disabled: "disabled",
} as const;
type RuleFilterValue = (typeof RuleFilter)[keyof typeof RuleFilter];

const RuleSort = {
	nameAsc: "nameAsc",
	nameDesc: "nameDesc",
	newest: "newest",
	oldest: "oldest",
} as const;
type RuleSortValue = (typeof RuleSort)[keyof typeof RuleSort];

export interface FormRulesListProps {
	rules: FormRule[];
	paths: AdminFormI18nPaths;
	onAdd: () => void;
	onEdit: (id: string) => void;
	onDelete: (id: string) => void;
	onToggleEnabled: (id: string, enabled: boolean) => void;
	isReadOnly?: boolean;
	/**
	 * When set to an element, All rules / sort / Add rule render into that node
	 * so they sit on the mode-tab row. `null` means the host is not mounted
	 * yet, so the toolbar stays inline until the element exists.
	 */
	actionsHost?: HTMLElement | null;
}

/**
 * Form Rules list. Filter, sort, and add live in the toolbar; each rule is one row.
 * Pure UI — no store access. The container handles URL state and store mutations.
 */
export function FormRulesList({
	rules,
	paths,
	onAdd,
	onEdit,
	onDelete,
	onToggleEnabled,
	isReadOnly,
	actionsHost,
}: FormRulesListProps) {
	const { t } = useAppTranslation();
	const [filter, setFilter] = useState<RuleFilterValue>(RuleFilter.all);
	const [sort, setSort] = useState<RuleSortValue>(RuleSort.nameAsc);

	const visibleRules = useMemo(() => {
		const filtered = rules.filter((r) => {
			const enabled = r.enabled ?? true;
			if (filter === RuleFilter.enabled) return enabled;
			if (filter === RuleFilter.disabled) return !enabled;
			return true;
		});
		const sorted = [...filtered];
		sorted.sort((a, b) => {
			switch (sort) {
				case RuleSort.nameAsc:
					return a.name.localeCompare(b.name);
				case RuleSort.nameDesc:
					return b.name.localeCompare(a.name);
				case RuleSort.newest:
					return (b.createdAt ?? "").localeCompare(a.createdAt ?? "");
				case RuleSort.oldest:
					return (a.createdAt ?? "").localeCompare(b.createdAt ?? "");
				default:
					return 0;
			}
		});
		return sorted;
	}, [rules, filter, sort]);

	const filterLabel =
		filter === RuleFilter.enabled
			? t(paths.formRules.filters.enabledOnly)
			: filter === RuleFilter.disabled
				? t(paths.formRules.filters.disabledOnly)
				: t(paths.formRules.filters.all);

	const actions: ReactNode = (
		<>
			<Dropdown.Root>
				<Button
					size="sm"
					color="secondary"
					iconTrailing={ChevronDown}
					aria-label={t(paths.formRules.filters.label)}
					data-testid="form-rules-filter"
				>
					{filterLabel}
				</Button>
				<Dropdown.Popover>
					<Dropdown.Menu
						selectionMode="single"
						selectedKeys={[filter]}
						onAction={(key) => {
							const next = String(key);
							if (
								next === RuleFilter.all ||
								next === RuleFilter.enabled ||
								next === RuleFilter.disabled
							) {
								setFilter(next);
							}
						}}
					>
						<Dropdown.Item id={RuleFilter.all} label={t(paths.formRules.filters.all)} />
						<Dropdown.Item
							id={RuleFilter.enabled}
							label={t(paths.formRules.filters.enabledOnly)}
						/>
						<Dropdown.Item
							id={RuleFilter.disabled}
							label={t(paths.formRules.filters.disabledOnly)}
						/>
					</Dropdown.Menu>
				</Dropdown.Popover>
			</Dropdown.Root>
			<Dropdown.Root>
				<Button
					size="sm"
					color="secondary"
					iconLeading={SwitchVertical01}
					iconTrailing={ChevronDown}
					aria-label={t(paths.formRules.sort.label)}
					data-testid="form-rules-sort"
					className="w-[68px] px-3!"
				/>
				<Dropdown.Popover>
					<Dropdown.Menu
						selectionMode="single"
						selectedKeys={[sort]}
						onAction={(key) => {
							const next = String(key);
							if (
								next === RuleSort.nameAsc ||
								next === RuleSort.nameDesc ||
								next === RuleSort.newest ||
								next === RuleSort.oldest
							) {
								setSort(next);
							}
						}}
					>
						<Dropdown.Item id={RuleSort.nameAsc} label={t(paths.formRules.sort.nameAsc)} />
						<Dropdown.Item id={RuleSort.nameDesc} label={t(paths.formRules.sort.nameDesc)} />
						<Dropdown.Item id={RuleSort.newest} label={t(paths.formRules.sort.newest)} />
						<Dropdown.Item id={RuleSort.oldest} label={t(paths.formRules.sort.oldest)} />
					</Dropdown.Menu>
				</Dropdown.Popover>
			</Dropdown.Root>
			{isReadOnly ? null : (
				<Button
					size="sm"
					type="button"
					iconLeading={Plus}
					data-testid="form-rules-add"
					className="bg-utility-orange-500 text-white hover:bg-utility-orange-600"
					onClick={onAdd}
				>
					{t(paths.formRules.addRule)}
				</Button>
			)}
		</>
	);

	const isEmpty = rules.length === 0;
	const isFilterEmpty = !isEmpty && visibleRules.length === 0;
	const actionsNode =
		actionsHost != null ? (
			createPortal(actions, actionsHost)
		) : (
			<div className="mb-4 flex items-center justify-end gap-2">{actions}</div>
		);

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			{actionsNode}
			{isEmpty ? (
				<div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
					<FormRulesFeaturedIcon className="mb-4" />
					<p className="text-sm font-medium text-primary">
						{t(paths.formRules.emptyTitle)}
					</p>
					<p className="mt-2 max-w-md text-sm text-tertiary">
						{t(paths.formRules.emptySubtitle)}
					</p>
				</div>
			) : isFilterEmpty ? (
				<p
					className="px-6 py-16 text-center text-sm text-tertiary"
					data-testid="form-rules-filter-empty"
				>
					{t(paths.formRules.filters.empty)}
				</p>
			) : (
				<ul className="flex flex-1 flex-col gap-2">
					{visibleRules.map((rule) => (
						<FormRuleCard
							key={rule.id}
							rule={rule}
							paths={paths}
							onEdit={onEdit}
							onDelete={onDelete}
							onToggleEnabled={onToggleEnabled}
							isReadOnly={isReadOnly}
						/>
					))}
				</ul>
			)}
		</div>
	);
}
